// ANT Modules
import { Form, Select } from "antd";

// Hooks
import { useCreateRetrospectiveClaim } from "containers/CreateRetrospectiveClaim/useCreateRetrospectiveClaim";

// Components
import { Head } from "components/molecules/Head/Head";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import FileUpload from "components/atoms/FileUpload/FileUpload";
import CreateClaimDetails from "components/organisms/CreateClaimDetails/CreateClaimDetails";
import Button from "components/atoms/Button/Button";
import Label from "components/atoms/Label/Label";
import Spinner from "components/atoms/Spinner/Spinner";

// Styles
import styles from "containers/CreateRetrospectiveClaim/createRetrospectiveClaim.module.css";

// Constants & Utils
import { PageTitles, CREATE_RETRO_CLAIM as Constants, CREATE_RETRO_CLAIM_FORM, RETRO_CLAIM } from "constants/global";
import getIcon from "utils/getIcon";
import StepInfo from "components/atoms/StepInfo/StepInfo";

const { Option } = Select;

const buttonTypeClass = (isEnabled: boolean) => {
	if (isEnabled) {
		return "active";
	}
	return "disabled";
};

const CreateRetrospectiveClaim = () => {
	const componentProps = useCreateRetrospectiveClaim();

	const {
		goBack,
		claimDetails,
		outletCode,
		fieldErrors,
		isNextEnabled,
		userInfo,
		storesDataList,
		loading,
		terminalsList,
		filteredOutletsList,
		terminal,
		form,
		handleTerminalSelect,
		validateFields,
		handleApprove,
		handleOutletSelect,
		handleOutletSearch,
		handleInputChange,
		handleOnDateChange,
		handleOnDateOk,
		handleSource,
		handlePaymentMode,
		handleUpload
	} = componentProps;

	const handlerFunction = (enabled: boolean, type: string) => {
		if (enabled) {
			switch (type) {
				case RETRO_CLAIM.PAGE_STATUS.APPROVE:
					return handleApprove;
				case RETRO_CLAIM.PAGE_STATUS.REJECT:
					return goBack;
			}
		}
		return undefined;
	};

	return (
		<div className={styles?.root}>
			{loading && <Spinner type="transparent" />}
			<Head>{PageTitles?.CREATE_RETROSPECTIVE_CLAIM}</Head>
			<PageTitle showBackIcon={true} title={Constants.PAGE_TITLE} subTitle={Constants.PAGE_SUBTITLE} goBack={goBack} />
			<div className={styles?.main_wrapper}>
				<div className="md:flex">
					<div className={`claim_file_upload ${styles?.upload_wrapper}`}>
						<FileUpload imageURL={claimDetails?.receipt_imgurl} handleUpload={handleUpload} />
					</div>
					<div className={styles?.create_claim_form_wrapper}>
						<div className={`create_claim_select ${styles?.create_claim_select_btn_wrapper}`}>
							<Form.Item name="source" label={<Label label={CREATE_RETRO_CLAIM_FORM.SOURCE_LABEL} classes={{ label: styles?.basic_input_label_source }} requiredMark />}>
								<Select
									defaultValue={claimDetails?.source ? claimDetails.source : CREATE_RETRO_CLAIM_FORM.SOURCE_SELECT_LABEL}
									onChange={handleSource}
									suffixIcon={getIcon({ icon: "caretDropDown" })}
									className={`${claimDetails?.source ? styles?.filled : ""}`}
								>
									<Option value={CREATE_RETRO_CLAIM_FORM.SOURCE_OPTION_1}>{CREATE_RETRO_CLAIM_FORM.SOURCE_OPTION_1}</Option>
								</Select>
							</Form.Item>
						</div>

						<CreateClaimDetails
							claimDetails={claimDetails}
							outletCode={outletCode}
							fieldErrors={fieldErrors}
							handleInputChange={handleInputChange}
							validateFields={validateFields}
							handleOutletSearch={handleOutletSearch}
							handleOutletSelect={handleOutletSelect}
							handlePaymentMode={handlePaymentMode}
							handleDateChange={handleOnDateChange}
							handleDateOk={handleOnDateOk}
							handleTerminalSelect={handleTerminalSelect}
							userInfo={userInfo}
							outletsList={storesDataList}
							form={form}
							terminal={terminal}
							terminalsList={terminalsList}
							filteredOutletsList={filteredOutletsList}
						/>
					</div>
				</div>
				<div className={styles?.bottom_wrapper}>
					<div className="md:w-[22.18rem] mb-3">
						<StepInfo message={Constants.MESSAGE} />
					</div>
					<div className="flex justify-end md:ml-[14rem]">
						<Button {...Constants.BUTTON_LABELS.CREATE_CLAIM} type={buttonTypeClass(isNextEnabled)} onClick={handlerFunction(isNextEnabled, RETRO_CLAIM.PAGE_STATUS.APPROVE)} />
						<span className="mx-[0.3125rem]"></span>
						<Button {...Constants.BUTTON_LABELS.CANCEL} onClick={handlerFunction(true, RETRO_CLAIM.PAGE_STATUS.REJECT)} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default CreateRetrospectiveClaim;
